/* ──────────────────────────────────────────────────────────────
   Sidebar/index.tsx · app sidebar nav
   ────────────────────────────────────────────────────────────── */
import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { useApp } from '../context/appContext';
import { useSession, roleLabel } from '../context/sessionContext';
import { navigateApp } from '../navigation/navigationRef';
import { useActiveView } from '../navigation/useActiveView';
import type { AppRouteName } from '../navigation/types';
import {
  Icon,
  Avatar,
  Popover,
  MenuItem,
  MenuSeparator,
} from '../components/ui';
import ProfileMenu from './profileMenu';

interface NavItem {
  id: AppRouteName;
  label: string;
  icon: string;
  count?: number;
  dot?: boolean;
}

interface NavGroup {
  group: string | null;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    group: null,
    items: [
      { id: 'log', label: 'Notification Log', icon: 'inbox' },
      { id: 'health', label: 'Health', icon: 'activity' },
      { id: 'test', label: 'Send Test', icon: 'send' },
    ],
  },
  {
    group: 'Admin',
    items: [
      { id: 'quotas', label: 'Quotas', icon: 'chart' },
      { id: 'apikeys', label: 'API Keys', icon: 'key' },
    ],
  },
  {
    // SKNOTIFICATION-GLOBAL-CR-001 — template governance.
    group: 'Governance',
    items: [
      { id: 'templates', label: 'Templates', icon: 'layers' },
      { id: 'governance', label: 'Consent & Suppression', icon: 'shield' },
    ],
  },
];

interface SidebarProps {
  onLogout?: () => void;
  /** Rendered inside the mobile overlay drawer — always full-width, never collapsed. */
  mobile?: boolean;
  /** Called after a nav item is tapped (used to close the mobile drawer). */
  onNavigate?: () => void;
}

export default function Sidebar({ onLogout, mobile = false, onNavigate }: SidebarProps) {
  const { theme, isDark, toggleTheme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const { collapsed, setCollapsed } = useApp();
  // In the mobile drawer we always show the expanded sidebar.
  const isCollapsed = mobile ? false : collapsed;
  const activeView = useActiveView();
  const { user, fullName, role } = useSession();
  const displayName = fullName || user?.email || 'Account';
  const displayRole = roleLabel(role) || 'Member';

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredChip, setHoveredChip] = useState(false);
  const [hoveredLogout, setHoveredLogout] = useState(false);

  // ── Styles ──────────────────────────────────────────────────

  const sidebarStyle: ViewStyle = {
    width: isCollapsed ? 64 : 248,
    backgroundColor: colors.paper2,
    borderRightWidth: 1,
    borderRightColor: colors.line,
    flexDirection: 'column',
    flexShrink: 0,
    // Web: allow the account hover-card to overflow past the rail (and stack
    // above the content). Native: keep it clipped.
    ...(Platform.OS === 'web' ? ({ overflow: 'visible', position: 'relative', zIndex: 50 } as any) : { overflow: 'hidden' }),
    // In the mobile overlay drawer, fill the height and float above content.
    ...(mobile ? { height: '100%' as any, ...(shadows.lg as ViewStyle) } : {}),
  };

  const brandStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    height: 56,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    flexShrink: 0,
  };

  const brandMarkStyle: ViewStyle = {
    width: 30,
    height: 30,
    borderRadius: radii.r2,
    backgroundColor: colors.ink,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const brandMarkTextStyle: TextStyle = {
    color: colors.card,
    fontWeight: fontWeight.bold,
    fontSize: 15,
    fontFamily: theme.font.mono,
    letterSpacing: -0.04 * 15,
    includeFontPadding: false,
  };

  const brandNameStyle: TextStyle = {
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.lg,
    color: colors.ink,
    letterSpacing: -0.01 * fontSize.lg,
    includeFontPadding: false,
    // @ts-ignore web-only
    ...(Platform.OS === 'web' ? { whiteSpace: 'nowrap' } : {}),
  };

  const brandSubStyle: TextStyle = {
    fontSize: fontSize.xs,
    color: colors.ink3,
    fontWeight: fontWeight.regular,
    letterSpacing: 0.02 * fontSize.xs,
    includeFontPadding: false,
  };

  const scrollStyle: ViewStyle = {
    flex: 1,
    paddingHorizontal: 10,
    paddingTop: 10,
    paddingBottom: 20,
  };

  const groupLabelStyle: TextStyle = {
    fontSize: fontSize.xs,
    letterSpacing: 0.06 * fontSize.xs,
    textTransform: 'uppercase',
    color: colors.ink4,
    fontWeight: fontWeight.semibold,
    paddingHorizontal: isCollapsed ? 0 : 10,
    paddingTop: 6,
    paddingBottom: 4,
    textAlign: isCollapsed ? 'center' : 'left',
    // @ts-ignore web-only
    ...(Platform.OS === 'web' ? { whiteSpace: 'nowrap' } : {}),
    includeFontPadding: false,
  };

  const getNavItemStyle = (id: string, isActive: boolean): ViewStyle => ({
    flexDirection: 'row',
    alignItems: 'center',
    gap: isCollapsed ? 0 : 10,
    paddingHorizontal: isCollapsed ? 0 : 10,
    paddingVertical: isCollapsed ? 9 : 8,
    borderRadius: radii.r2,
    backgroundColor: isActive
      ? colors.card
      : hoveredItem === id
      ? (Platform.OS === 'web' ? 'rgba(30,38,53,0.05)' : 'transparent')
      : 'transparent',
    borderWidth: 1,
    borderColor: isActive ? colors.line : 'transparent',
    justifyContent: isCollapsed ? 'center' : 'flex-start',
    ...(isActive ? (shadows.sm as ViewStyle) : {}),
    ...Platform.select({
      web: { cursor: 'pointer', userSelect: 'none', transition: 'background 80ms, color 80ms', whiteSpace: 'nowrap' } as ViewStyle,
      default: {},
    }),
  });

  const getNavLabelStyle = (isActive: boolean): TextStyle => ({
    flex: 1,
    fontSize: fontSize.md,
    color: (isActive || hoveredItem !== null) ? colors.ink : colors.ink2,
    fontWeight: isActive ? fontWeight.semibold : fontWeight.medium,
    includeFontPadding: false,
    // @ts-ignore web-only
    ...(Platform.OS === 'web' ? { whiteSpace: 'nowrap' } : {}),
  });

  const navIconStyle: ViewStyle = {
    width: 18,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const navCountStyle: ViewStyle = {
    marginLeft: 'auto',
    backgroundColor: colors.paper3,
    borderRadius: 999,
    paddingHorizontal: 7,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  };

  const navCountTextStyle: TextStyle = {
    fontFamily: theme.font.mono,
    fontSize: fontSize.xs,
    color: colors.ink3,
    includeFontPadding: false,
  };

  const navDotStyle: ViewStyle = {
    marginLeft: 'auto',
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: colors.warn,
  };

  const footStyle: ViewStyle = {
    borderTopWidth: 1,
    borderTopColor: colors.line,
    padding: 10,
    flexShrink: 0,
  };

  const userChipStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 8,
    paddingVertical: 7,
    borderRadius: radii.r2,
    borderWidth: 1,
    borderColor: 'transparent',
    backgroundColor: hoveredChip ? 'rgba(30,38,53,0.05)' : 'transparent',
    ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <View style={sidebarStyle}>
      {/* Brand */}
      <View style={brandStyle}>
        <View style={brandMarkStyle}>
          <Text style={brandMarkTextStyle}>SK</Text>
        </View>
        {!isCollapsed && (
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={brandNameStyle} numberOfLines={1}>SK Notification</Text>
            <Text style={brandSubStyle} numberOfLines={1}>Delivery console</Text>
          </View>
        )}
      </View>

      {/* Nav scroll */}
      <ScrollView style={scrollStyle} showsVerticalScrollIndicator={false}>
        {NAV.map((group, gi) => (
          <View key={gi} style={{ marginBottom: 14 }}>
            {group.group && (
              <Text style={groupLabelStyle}>{group.group}</Text>
            )}
            {group.items.map((item) => {
              const isActive = activeView === item.id;
              return (
                <TouchableOpacity
                  key={item.id}
                  style={getNavItemStyle(item.id, isActive)}
                  onPress={() => { navigateApp(item.id); onNavigate?.(); }}
                  activeOpacity={0.75}
                  accessibilityLabel={item.label}
                  {...(Platform.OS === 'web'
                    ? {
                        onMouseEnter: () => setHoveredItem(item.id),
                        onMouseLeave: () => setHoveredItem(null),
                      } as any
                    : {})}
                >
                  <View style={navIconStyle}>
                    <Icon
                      name={item.icon}
                      size={17}
                      color={isActive || hoveredItem === item.id ? colors.ink : colors.ink2}
                    />
                  </View>
                  {!isCollapsed && (
                    <Text style={getNavLabelStyle(isActive)} numberOfLines={1}>
                      {item.label}
                    </Text>
                  )}
                  {!isCollapsed && item.count !== undefined && (
                    <View style={navCountStyle}>
                      <Text style={navCountTextStyle}>{item.count}</Text>
                    </View>
                  )}
                  {!isCollapsed && item.dot && (
                    <View style={navDotStyle} accessibilityLabel="Needs attention" />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        ))}
      </ScrollView>

      {/* Footer */}
      <View style={footStyle}>
        {/* Light / dark mode toggle — sits above the account chip. */}
        <TouchableOpacity
          onPress={toggleTheme}
          activeOpacity={0.75}
          accessibilityLabel={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            paddingVertical: 9,
            paddingHorizontal: 10,
            borderRadius: radii.r2,
            marginBottom: 6,
            ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
          }}
        >
          <Icon name={isDark ? 'sun' : 'moon'} size={16} color={colors.ink3} />
          {!isCollapsed && (
            <Text style={{ flex: 1, fontSize: fontSize.md, color: colors.ink2, fontWeight: fontWeight.medium, includeFontPadding: false }}>
              {isDark ? 'Light mode' : 'Dark mode'}
            </Text>
          )}
        </TouchableOpacity>

        {/* Account — avatar + name + email, opens a card with Log out. */}
        <ProfileMenu variant="sidebar" onLogout={onLogout} compact={isCollapsed} />
      </View>
    </View>
  );
}
